/**
 * QR Customer Reservation modal — masa rezervasyonu yapma.
 *
 * Customer JWT yoksa önce CustomerLoginModal'a yönlendir (parent yöneter).
 */
import { Button, Modal, NumberInput, Stack, TextInput, Text, Alert, Textarea } from "@mantine/core";
import { IconAlertCircle, IconCalendar } from "@tabler/icons-react";
import { DateTimePicker } from "@mantine/dates";
import { useState } from "react";
import axios from "axios";
import { notifications } from "@mantine/notifications";
import { validatePhoneTR } from "../../shared/ui/validators";

const API_URL = import.meta.env["VITE_API_URL"] || "http://localhost:8000";

interface Props {
  opened: boolean;
  onClose: () => void;
  qrToken: string;
}

export function ReservationModal({ opened, onClose, qrToken }: Props) {
  const [partySize, setPartySize] = useState<number>(2);
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit() {
    setError(null);
    const phoneErr = validatePhoneTR(phone);
    if (phoneErr) {
      setError(phoneErr);
      return;
    }
    setLoading(true);
    try {
      // Public reservation endpoint yok — şu an Customer JWT gerek
      const token = localStorage.getItem("customer_token");
      if (!token) {
        setError("Önce giriş yapmalısınız (Üye Ol / Giriş)");
        return;
      }
      await axios.post(
        `${API_URL}/api/v1/reservations/`,
        {
          party_size: partySize,
          starts_at: startsAt?.toISOString(),
          customer_name: name,
          customer_phone: phone,
          notes,
          qr_token: qrToken,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      notifications.show({ message: "Rezervasyon talebi alındı, kapora gerekirse SMS gelecek", color: "green" });
      onClose();
      setStartsAt(null); setNotes("");
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Rezervasyon yapılamadı");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Rezervasyon Yap" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Restorana gelmek istediğiniz tarih ve saati seçin. Kapora gerekirse
          telefonunuza SMS gönderilir.
        </Text>

        {error && <Alert color="red" icon={<IconAlertCircle size={16} />}>{error}</Alert>}

        <NumberInput
          label="Kişi Sayısı"
          required
          value={partySize}
          onChange={(v) => setPartySize(Number(v) || 1)}
          min={1}
          max={20}
          size="lg"
        />
        <DateTimePicker
          label="Tarih ve Saat"
          required
          value={startsAt}
          onChange={(v) => setStartsAt(v as any)}
          minDate={new Date()}
          size="lg"
        />
        <TextInput
          label="Adınız"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          size="lg"
        />
        <TextInput
          label="Telefon"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
          placeholder="+90 5XX XXX XX XX"
          size="lg"
        />
        <Textarea
          label="Not (opsiyonel)"
          placeholder="Pencere kenarı, sürpriz parti vb..."
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        <Button
          onClick={submit}
          loading={loading}
          disabled={!startsAt || partySize < 1}
          size="lg"
          leftSection={<IconCalendar size={18} />}
          fullWidth
        >
          Rezervasyonu Gönder
        </Button>
      </Stack>
    </Modal>
  );
}
